import React, { useState, useRef, useEffect } from 'react';
import { X, User, Mail, Building2 } from 'lucide-react';
import { useClientsAndContacts, EmailContact } from '../../hooks/useClientsAndContacts';

interface EmailAutoCompleteProps {
  selectedEmails: string[];
  onEmailsChange: (emails: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function EmailAutoComplete({
  selectedEmails,
  onEmailsChange,
  placeholder = 'Aggiungi partecipanti per email...',
  className = '',
  disabled = false
}: EmailAutoCompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<EmailContact[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { contacts, loading, searchContacts } = useClientsAndContacts();

  // Update suggestions when contacts or input value changes
  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions(contacts.slice(0, 10)); // Show first 10 contacts when no search
    } else {
      // Search contacts that match input value
      const filtered = contacts.filter(contact =>
        contact.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        contact.email.toLowerCase().includes(inputValue.toLowerCase()) ||
        (contact.clientName && contact.clientName.toLowerCase().includes(inputValue.toLowerCase()))
      ).slice(0, 10);

      setSuggestions(filtered);
    }
  }, [contacts, inputValue]);

  // Debounced search when user types
  useEffect(() => {
    if (inputValue.trim().length > 2) {
      const timeoutId = setTimeout(() => {
        searchContacts(inputValue);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [inputValue, searchContacts]);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const addEmail = (email: string, fromSuggestion = false) => {
    if (!email.trim()) return;

    const trimmedEmail = email.trim().toLowerCase();

    // Validate email format if not from suggestion
    if (!fromSuggestion && !isValidEmail(trimmedEmail)) {
      return;
    }

    // Check if email is already selected
    if (selectedEmails.includes(trimmedEmail)) {
      setInputValue('');
      return;
    }

    onEmailsChange([...selectedEmails, trimmedEmail]);
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const removeEmail = (emailToRemove: string) => {
    onEmailsChange(selectedEmails.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          addEmail(suggestions[highlightedIndex].email, true);
        } else if (inputValue.trim()) {
          addEmail(inputValue);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;

      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      case 'Tab':
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          e.preventDefault();
          addEmail(suggestions[highlightedIndex].email, true);
        }
        break;

      case 'Backspace':
        if (!inputValue && selectedEmails.length > 0) {
          removeEmail(selectedEmails[selectedEmails.length - 1]);
        }
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Handle comma or semicolon separated emails
    if (value.includes(',') || value.includes(';')) {
      const emails = value.split(/[,;]/).map(email => email.trim()).filter(Boolean);
      emails.forEach(email => addEmail(email));
      setInputValue('');
    }
  };

  const handleSuggestionClick = (contact: EmailContact) => {
    addEmail(contact.email, true);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay closing to allow clicking on suggestions
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="min-h-[42px] p-2 glass-card rounded-lg focus-within:ring-2 focus-within:ring-black focus-within:border-transparent">
        <div className="flex flex-wrap gap-1 items-center">
          {/* Selected emails */}
          {selectedEmails.map((email) => (
            <div
              key={email}
              className="flex items-center gap-1 px-2 py-1 bg-black/10 rounded-md text-xs"
            >
              <Mail className="h-3 w-3" />
              <span>{email}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  className="hover:bg-black/10 rounded p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}

          {/* Input field */}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={selectedEmails.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-black placeholder-black/40 text-sm"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Suggestions dropdown */}
      {isOpen && !disabled && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-black/10 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-black/60">
              Caricamento contatti...
            </div>
          ) : (
            <ul ref={listRef} className="py-1">
              {suggestions.map((contact, index) => (
                <li
                  key={contact.id}
                  className={`px-3 py-2 cursor-pointer transition-colors ${
                    index === highlightedIndex
                      ? 'bg-black/5'
                      : 'hover:bg-black/5'
                  }`}
                  onClick={() => handleSuggestionClick(contact)}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      {contact.type === 'client' ? (
                        <Building2 className="h-4 w-4 text-blue-600" />
                      ) : (
                        <User className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-black truncate">
                        {contact.name}
                      </div>
                      <div className="text-xs text-black/60 truncate">
                        {contact.email}
                        {contact.clientName && (
                          <span className="ml-2">• {contact.clientName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}

              {/* Show option to add custom email if input looks like an email */}
              {inputValue && isValidEmail(inputValue) && !suggestions.some(s => s.email === inputValue.toLowerCase()) && (
                <li
                  className={`px-3 py-2 cursor-pointer transition-colors border-t border-black/5 ${
                    highlightedIndex === suggestions.length
                      ? 'bg-black/5'
                      : 'hover:bg-black/5'
                  }`}
                  onClick={() => addEmail(inputValue)}
                >
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <div className="flex-1">
                      <div className="text-sm text-black">
                        Aggiungi "{inputValue}"
                      </div>
                      <div className="text-xs text-black/60">
                        Email personalizzata
                      </div>
                    </div>
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}